import { useState, type FormEvent } from "react";
import { askText } from "../../confirm";
import type { DefinitionDetail, DefinitionUsage, FieldDefinition, FieldKind } from "@workcore/schemas";
import { Panel } from "../../ui";
import { InlineText, WriteError, parseValue, plural, toKey, useDefinitionWrite, useFieldTemplates } from "./shared";

const KINDS: FieldKind[] = ["text", "rich_text", "integer", "decimal", "boolean", "date", "datetime", "single_select", "multi_select", "user", "object_ref", "contact", "url"];
const isSelect = (k: FieldKind) => k === "single_select" || k === "multi_select";

function Options({ slug, def, f, canEdit }: { slug: string; def: DefinitionDetail; f: FieldDefinition; canEdit: boolean }) {
  const write = useDefinitionWrite(slug, def.id);
  const [label, setLabel] = useState("");
  return (
    <div className="options">
      <ul className="chips">
        {f.options.map((o) => (
          <li key={o.id} className={o.archived ? "archived" : ""}>
            <InlineText label={`option ${o.key}`} value={o.label} disabled={!canEdit || o.archived} onSave={(v) => write.mutate({ method: "PATCH", path: `/v1/ws/${slug}/options/${o.id}`, body: { label: v } })} />
            <code className="small">{o.key}</code>
            {canEdit && (
              <button
                className="link"
                aria-label={o.archived ? `Restore ${o.label}` : `Archive ${o.label}`}
                onClick={() => write.mutate({ method: "PATCH", path: `/v1/ws/${slug}/options/${o.id}`, body: { archived: !o.archived }, ...(o.archived ? {} : { impact: `Archive option '${o.label}'. Objects that have it keep it; it can no longer be chosen.` }) })}
              >
                {o.archived ? "restore" : "×"}
              </button>
            )}
          </li>
        ))}
      </ul>
      {canEdit && (
        <form autoComplete="off" className="inline" onSubmit={(e: FormEvent) => (e.preventDefault(), write.mutate({ method: "POST", path: `/v1/ws/${slug}/fields/${f.id}/options`, body: { key: toKey(label).replace(/_/g, "-"), label } }, { onSuccess: () => setLabel("") }))}>
          <input aria-label={`New option for ${f.label}`} placeholder="add option" required value={label} onChange={(e) => setLabel(e.target.value)} />
          <button type="submit" className="secondary">Add</button>
        </form>
      )}
      <WriteError error={write.error} />
    </div>
  );
}

function FieldRow({ slug, def, f, usage, canEdit }: { slug: string; def: DefinitionDetail; f: FieldDefinition; usage: DefinitionUsage | undefined; canEdit: boolean }) {
  const write = useDefinitionWrite(slug, def.id);
  const u = usage?.fields.find((x) => x.fieldId === f.id);
  const hidden = f.status === "hidden";
  const patch = (body: unknown, impact?: string) => write.mutate({ method: "PATCH", path: `/v1/ws/${slug}/fields/${f.id}`, body, ...(impact ? { impact } : {}) });
  const toggleRequired = () => {
    if (f.required) return patch({ required: false });
    const missing = u?.missing ?? 0;
    if (missing === 0) return patch({ required: true });
    void askText({
      title: `Make '${f.label}' required?`,
      body: `${plural(missing, "object")} ${missing === 1 ? "has" : "have"} no ${f.label}. Making it required fills in a default on them.`,
      action: "Next",
      input: { label: "Default value", value: "" },
    }).then((raw) => {
      if (raw === undefined || raw.trim() === "") return;
      patch({ required: true, defaultValue: parseValue(f.kind, raw) }, `Make '${f.label}' required and fill in the default on ${plural(missing, "object")}.`);
    });
  };
  const setBinding = (typeId: number, on: boolean) => {
    const typeIds = on ? [...f.typeIds, typeId] : f.typeIds.filter((t) => t !== typeId);
    const t = def.types.find((x) => x.id === typeId)!;
    write.mutate({
      method: "PUT",
      path: `/v1/ws/${slug}/fields/${f.id}/bindings`,
      body: { typeIds },
      ...(on ? {} : { impact: `Stop using '${f.label}' on ${t.plural}. Values already stored are kept but no longer shown or editable there.` }),
    });
  };
  return (
    <Panel
      title={f.label}
      actions={
        <span className="actions">
          <code>{f.key}</code>
          <span className="badge">{f.kind.replace("_", " ")}</span>
          {f.templateKey && <span className="tag">from template</span>}
          {hidden && <span className="badge badge-archived">hidden</span>}
          {canEdit && (
            <button className="link" onClick={() => patch({ status: hidden ? "active" : "hidden" }, hidden ? undefined : `Hide '${f.label}'. Its values are kept and come back if you unhide it.`)}>
              {hidden ? "Unhide" : "Hide"}
            </button>
          )}
        </span>
      }
    >
      <div className="field-grid">
        <span className="muted">Label</span>
        <InlineText label={`${f.label} label`} value={f.label} disabled={!canEdit} onSave={(v) => patch({ label: v })} />
        <span className="muted">Description</span>
        <InlineText label={`${f.label} description`} value={f.description} disabled={!canEdit} onSave={(v) => patch({ description: v })} />
        <span className="muted">Required</span>
        <label className="check">
          <input type="checkbox" disabled={!canEdit || hidden} checked={f.required} onChange={toggleRequired} /> {f.required ? "yes" : "no"}
          {f.defaultValue !== null && f.defaultValue !== undefined && <span className="muted small"> · default {JSON.stringify(f.defaultValue)}</span>}
        </label>
        <span className="muted">Used on</span>
        <span>
          {def.types.map((t) => (
            <label key={t.id} className="check">
              <input type="checkbox" disabled={!canEdit || hidden} checked={f.typeIds.includes(t.id)} onChange={(e) => setBinding(t.id, e.target.checked)} /> {t.name}
            </label>
          ))}
        </span>
        <span className="muted">Data</span>
        <span>{u ? `${u.withValue} with a value, ${u.missing} without` : "…"}</span>
      </div>
      {isSelect(f.kind) && <Options slug={slug} def={def} f={f} canEdit={canEdit && !hidden} />}
      <WriteError error={write.error} />
    </Panel>
  );
}

function AddField({ slug, def }: { slug: string; def: DefinitionDetail }) {
  const write = useDefinitionWrite(slug, def.id);
  const templates = useFieldTemplates(slug).data ?? [];
  const [mode, setMode] = useState<"new" | "template">("new");
  const [label, setLabel] = useState("");
  const [kind, setKind] = useState<FieldKind>("text");
  const [options, setOptions] = useState("");
  const [template, setTemplate] = useState("");
  const [types, setTypes] = useState<number[]>([]);
  const reset = () => (setLabel(""), setOptions(""), setTypes([]), setTemplate(""));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (mode === "template") {
      write.mutate({ method: "POST", path: `/v1/ws/${slug}/definitions/${def.id}/fields/from-template`, body: { templateKey: template, typeIds: types } }, { onSuccess: reset });
      return;
    }
    const opts = isSelect(kind) ? options.split(",").map((o) => o.trim()).filter(Boolean).map((o) => ({ key: toKey(o).replace(/_/g, "-"), label: o })) : undefined;
    write.mutate({ method: "POST", path: `/v1/ws/${slug}/definitions/${def.id}/fields`, body: { key: toKey(label), label, kind, typeIds: types, ...(opts ? { options: opts } : {}) } }, { onSuccess: reset });
  };
  return (
    <Panel title="Add a field">
      <div className="row">
        <label className="check"><input type="radio" checked={mode === "new"} onChange={() => setMode("new")} /> new field</label>
        <label className="check"><input type="radio" checked={mode === "template"} onChange={() => setMode("template")} /> from a template</label>
      </div>
      <form autoComplete="off" className="form wide" onSubmit={submit}>
        {mode === "new" ? (
          <div className="inline wrap">
            <label className="inline-field"><span>Label</span><input aria-label="Field label" placeholder="e.g. Business value" required value={label} onChange={(e) => setLabel(e.target.value)} /></label>
            <label className="inline-field"><span>Kind</span><select aria-label="Field kind" value={kind} onChange={(e) => setKind(e.target.value as FieldKind)}>
              {KINDS.map((k) => <option key={k} value={k}>{k.replace("_", " ")}</option>)}
            </select></label>
            {isSelect(kind) && <label className="inline-field"><span>Options</span><input aria-label="Options" placeholder="comma separated" value={options} onChange={(e) => setOptions(e.target.value)} /></label>}
            <span className="muted small">key: {toKey(label) || "…"} (never changes)</span>
          </div>
        ) : (
          <label className="inline-field"><span>Template</span><select aria-label="Template" required value={template} onChange={(e) => setTemplate(e.target.value)}>
            <option value="">choose a template…</option>
            {templates.map((t) => <option key={t.key} value={t.key}>{t.label}: {t.description}</option>)}
          </select></label>
        )}
        <div>
          <span className="muted">Use on: </span>
          {def.types.map((t) => (
            <label key={t.id} className="check">
              <input type="checkbox" checked={types.includes(t.id)} onChange={(e) => setTypes(e.target.checked ? [...types, t.id] : types.filter((x) => x !== t.id))} /> {t.name}
            </label>
          ))}
        </div>
        <div><button type="submit" disabled={write.isPending}>Add field</button></div>
      </form>
      <WriteError error={write.error} />
    </Panel>
  );
}

export function FieldsTab({ slug, def, usage, canEdit }: { slug: string; def: DefinitionDetail; usage: DefinitionUsage | undefined; canEdit: boolean }) {
  const active = def.fields.filter((f) => f.status === "active");
  const hidden = def.fields.filter((f) => f.status === "hidden");
  return (
    <>
      <p className="muted">Custom fields sit alongside the base properties every object has (name, description, dates, estimate, owner, assignees, labels, state). A field's kind can never change; create a new field instead.</p>
      {canEdit && <AddField slug={slug} def={def} />}
      {active.map((f) => <FieldRow key={f.id} slug={slug} def={def} f={f} usage={usage} canEdit={canEdit} />)}
      {hidden.length > 0 && <h3 className="muted">Hidden</h3>}
      {hidden.map((f) => <FieldRow key={f.id} slug={slug} def={def} f={f} usage={usage} canEdit={canEdit} />)}
    </>
  );
}
